/** The `flowErrors` area: the refusals of flows and their stages, definitions, flow rules, ceremonies and decisions (src/domain/flow-definition.ts, stage-gates.ts, flows.ts, definitions.ts, src/server/flows.ts, definitions.ts, ceremonies.ts), as descriptors a surface formats
 *  (docs/I18N.md §6). The English is the refusal's `message`, unchanged. */
export const flowErrors = {
  // --- a stage's means pairs (src/domain/stage-gates.ts) ---------------------
  'flowErrors.means.notObject': 'A means pair must be an object.',
  /** {attr}: the atom's stable name (delivered, closed, active). */
  'flowErrors.means.needsYesNo': 'A {attr} pair needs a yes/no value.',
  /** {values}: the verdict values, joined with " | ". */
  'flowErrors.means.verdictValue': 'A verdict pair must be one of {values}.',
  'flowErrors.means.signalKey': 'A signal pair needs a catalog key.',
  'flowErrors.means.signalValue': 'A signal pair is worn or clear.',
  'flowErrors.means.fieldKey': 'A field pair needs a field key.',
  'flowErrors.means.fieldScalar': 'A field pair sets a scalar (string or number).',
  'flowErrors.means.fieldEmpty': 'A field pair needs a value (empty is not a value).',
  'flowErrors.means.unknownAttr':
    'A means pair values done, closed, active, verdict, a declared tag, or a field - nothing else exists.',
  /** The attribute a pair values, as its described pair names it
   *  (`meansPairName`): "means names Done twice". */
  'flowErrors.pairName.delivered': 'Completion',
  'flowErrors.pairName.closed': 'Conclusion',
  'flowErrors.pairName.active': 'Active',
  'flowErrors.pairName.verdict': 'Verdict',

  // --- a stage row ------------------------------------------------------------
  /** {what}: the value, as one of the `flowErrors.what.*` phrases. */
  'flowErrors.value.notString': '{what} must be a string.',
  'flowErrors.what.gatekeeper': 'Stage "{stage}" gatekeeper',
  'flowErrors.what.workDescription': 'Stage "{stage}" work description',
  'flowErrors.what.workCriteria': 'Stage "{stage}" work criteria',
  'flowErrors.what.workContributor': 'Stage "{stage}" work contributor',
  'flowErrors.stage.notObject': 'A stage must be an object.',
  'flowErrors.stage.needsKey': 'A stage needs a stable key.',
  'flowErrors.stage.needsLabel': 'Stage "{stage}" needs a label.',
  'flowErrors.stage.spinePhaseRetired':
    'Stage "{stage}": spinePhase is retired - a stage\'s meaning is its `means` pairs (docs/FLOW_DEFINITION.md §11).',
  'flowErrors.stage.meansNotList': 'Stage "{stage}": means must be a list of attribute pairs.',
  /** Another refusal, inside one stage: {error} is that refusal. */
  'flowErrors.stage.wrapped': 'Stage "{stage}": {error}',
  /** {attribute}: `flowErrors.pairName.*`, or a tag's or field's word. */
  'flowErrors.stage.meansTwice': 'Stage "{stage}": means names {attribute} twice.',
  'flowErrors.stage.gateMode': 'Stage "{stage}": gate mode must be open or enforced.',
  'flowErrors.stage.gateCondition': 'Stage "{stage}": gate condition must be a list of item ids.',
  'flowErrors.stage.requiredFields':
    'Stage "{stage}": requiredFields must be a list of field keys.',
  'flowErrors.stage.workNotList': 'Stage "{stage}": gate work must be a list of templates.',
  'flowErrors.stage.workNotObject': 'Stage "{stage}": each work template must be an object.',
  'flowErrors.stage.workNeedsKey': 'Stage "{stage}": a work template needs a stable key.',
  'flowErrors.stage.workDuplicateKey': 'Stage "{stage}": duplicate work template key "{template}".',
  'flowErrors.stage.workNeedsTitle': 'Stage "{stage}": work template "{template}" needs a title.',
  'flowErrors.stage.workMandatory':
    'Stage "{stage}": work template "{template}" mandatory must be a boolean.',
  'flowErrors.stage.workSeeds': 'Stage "{stage}": work template seeds must be an object.',
  'flowErrors.stages.notList': 'Stages must be a list.',
  'flowErrors.stages.duplicateKey': 'Duplicate stage key "{stage}".',
  // --- a flow definition (src/domain/flow-definition.ts) -------------------
  'flowErrors.who.notObject': 'A Who must be an object.',
  /** {roles}: the roles a Who may name, joined with " | ". */
  'flowErrors.who.seatRole': 'A seat Who needs a role ({roles}).',
  'flowErrors.who.groupRole': 'A group-role Who needs a role ({roles}).',
  'flowErrors.who.unknownKind':
    'A Who selects a seat, a group role, or anyone-with-write - nothing else exists.',
  'flowErrors.dispatch.policiesNotObject': "A dispatch action's policies are an object.",
  /** {question}: a question class's stable key ("open-children"). */
  'flowErrors.dispatch.unknownQuestion': '"{question}" is not a question an automation can hit.',
  'flowErrors.dispatch.notCommand': 'A dispatch action runs a canonical command.',
  'flowErrors.dispatch.setActiveBoolean': 'A SetActive action needs an `active` boolean.',
  'flowErrors.dispatch.unknownCommand':
    'A dispatch action runs a canonical command (Tender / Retract / Close / Reopen / SetActive / Accept / Dispute / Resolve).',
  'flowErrors.action.notObject': 'An action must be an object.',
  /** {kind}: the action's stable kind (declare, clear). */
  'flowErrors.action.needsTagKey': 'A {kind} action needs a catalog key.',
  'flowErrors.action.setFieldKeys': 'A set-field action needs a moduleKey and a fieldKey.',
  'flowErrors.action.setFieldScalar': 'A set-field action sets a scalar (string, number, null).',
  'flowErrors.action.unknownKind':
    'An action dispatches a canonical command, declares/clears a catalog state, or sets a field.',
  /** {block}: the block's stable name (thenIn, thenOut). */
  'flowErrors.action.notList': '{block} must be a list of actions.',
  'flowErrors.stage.targetRetired':
    'The separate target predicate is retired - a stage pulls on its `means` pairs (docs/FLOW_DEFINITION.md §11).',
  /** A stage's When or derive-predicate holds the filter value for the person
   *  looking (`self`, shown as "me"), which no flow evaluation has. */
  'flowErrors.predicate.self':
    'A flow cannot filter by "me": it checks an item for no one in particular. Name the person or the group instead.',
  'flowErrors.definition.notObject': 'A flow definition must be an object.',
  'flowErrors.definition.nameNotString': 'A flow name must be a string.',
  'flowErrors.factRules.notList': 'Fact rules must be a list.',
  /** {fact}: the fact's word (`FACT_RULE_WORD`). */
  'flowErrors.factRules.twoRules': 'Two rules grade "{fact}" - one flow, one word per fact.',
  /** {condition}: the rule's line (`factRuleCondition`), "carries #legal". */
  'flowErrors.factRules.twoConditionRules':
    'Two rules grade "{condition}" - one flow, one word per tag or stage.',
  'flowErrors.factRules.unknownStage':
    'A rule names stage "{stage}", which this flow does not have.',
  /** Where a reference sits: {block} is the block's stable name (whenIn,
   *  thenOut, means, ...). */
  'flowErrors.where.stageBlock': 'Stage "{stage}" {block}',
  'flowErrors.ref.unknownFlowField': '{where} references unknown flow field "{field}".',
  'flowErrors.ref.unknownModule': '{where} references unknown module "{module}".',
  'flowErrors.ref.unknownField': '{where} references unknown field "{module}.{field}".',
  'flowErrors.ref.unknownTag': '{where} references unknown catalog key "{tag}".',
  'flowErrors.stage.requiresUnknownField': 'Stage "{stage}" requires unknown flow field "{field}".',
  /** {means}: the stage's pairs, described (`describeMeans`). */
  'flowErrors.means.unreachable':
    'Stage "{stage}"\'s meaning can never hold: no reachable state satisfies {means}.',
  'flowErrors.means.duplicate':
    'Stages "{a}" and "{b}" mean exactly the same ({means}) - two words for one state cannot both pull. Change one meaning, or make one a manual word (no pairs).',
  /** {example}: a state both meanings hold under, described. */
  'flowErrors.means.collision':
    'Stages "{a}" and "{b}" can mean the same state - both hold at once under {example}. Meanings must be exclusive so the pull always has exactly one destination.',
  /** {where}: `flowErrors.where.stageBlock`; {command}: the canonical command. */
  'flowErrors.policy.unreachable':
    '{where}: a {command} cannot hit the "{question}" question; remove its policy.',

  // --- an automation's policies (src/domain/resolution/policies.ts) --------
  'flowErrors.policy.cascadeSize':
    'The "cascade-size" question is a refusal; no policy answers it.',
  /** {answers}: the answers the question offers, joined with ", ". */
  'flowErrors.policy.notAnAnswer':
    '"{answer}" is not an answer to the "{question}" question ({answers}).',
  'flowErrors.policy.shape': 'A "{question}" policy is an answer, "propose" or "skip".',
  /** A policy as a finding names it (`describePolicy`). */
  'flowErrors.policy.word.skip': 'skip',
  'flowErrors.policy.word.propose': 'propose',
  'flowErrors.policy.word.answer': 'answer "{answer}"',
  'flowErrors.policy.word.none': 'none',

  // --- a fact rule (src/domain/fact-rules.ts) --------------------------------
  /** {where}: which rule (`flowErrors.where.factRule` / `boardRule`). */
  'flowErrors.factRule.notObject': '{where} must be an object.',
  'flowErrors.factRule.unknownFact': '{where} names an unknown fact.',
  'flowErrors.factRule.unknownGrade': '{where} names an unknown grade.',
  'flowErrors.factRule.wholeDays': '{where} needs a whole number of days.',
  'flowErrors.factRule.needsWord': '{where} needs a word.',
  'flowErrors.factRule.needsTag': '{where} needs a tag.',
  'flowErrors.factRule.needsStage': '{where} needs a stage.',
  'flowErrors.factRule.conditionNoDays':
    '{where} names a tag or a stage, which takes no number of days.',
  'flowErrors.factRule.needsThreshold':
    '{where} grades a silence, so it needs a number of days: nothing here knows how long "{fact}" may run before it matters.',
  'flowErrors.where.factRule': 'A fact rule',
  'flowErrors.where.boardRule': 'A board rule',

  // --- a stage move, disabled with its reason (decideStageMove) -------------
  /** {who}: `flowErrors.who.*` (describeWho). */
  'flowErrors.move.whoInto': 'Only {who} may move items into "{stage}".',
  'flowErrors.move.whoOutOf': 'Only {who} may move items out of "{stage}".',
  /** {criteria}: the unmet criteria, joined (`flowErrors.criterion.joined`). */
  'flowErrors.move.leaving': 'Leaving "{stage}" needs: {criteria}.',
  'flowErrors.move.leavingOneOf': 'Leaving "{stage}" needs one of: {criteria}.',
  'flowErrors.move.entering': 'Entering "{stage}" needs: {criteria}.',
  'flowErrors.move.enteringOneOf': 'Entering "{stage}" needs one of: {criteria}.',
  /** The catch-all for a move the reader may not learn more about. */
  'flowErrors.move.notAvailable': 'Not available.',
  'flowErrors.move.notAllowed': 'This move is not allowed.',
  'flowErrors.who.anyone': 'anyone with write access',
  'flowErrors.who.requester': 'a requester of this item',
  'flowErrors.who.contributor': 'a contributor of this item',
  'flowErrors.who.owner': 'an owner of this flow',
  'flowErrors.who.member': 'a member of this flow',
  /** One criterion in plain words (`describeCriterion`). */
  'flowErrors.criterion.joined': '{first}, {second}',
  /** Two values of one criterion; a longer list nests ("a or b or c"). */
  'flowErrors.criterion.or': '{first} or {second}',
  'flowErrors.criterion.not': 'not {criterion}',
  'flowErrors.criterion.delivered': 'in review or done',
  'flowErrors.criterion.notDelivered': 'open or cancelled',
  'flowErrors.criterion.noVerdict': 'no verdict yet',
  'flowErrors.criterion.accepted': 'accepted',
  'flowErrors.criterion.disputed': 'disputed',
  'flowErrors.criterion.cancelled': 'cancelled',
  'flowErrors.criterion.notCancelled': 'not cancelled',
  'flowErrors.criterion.done': 'done',
  'flowErrors.criterion.notDone': 'not done',
  'flowErrors.criterion.requesterSeated': 'a requester seated',
  'flowErrors.criterion.contributorSeated': 'a contributor seated',
  'flowErrors.criterion.noRequesterSeated': 'no requester seated',
  'flowErrors.criterion.noContributorSeated': 'no contributor seated',
  /** {ref}: the seat holder's ref, as the criterion stores it. */
  'flowErrors.criterion.requesterIs': 'requester {ref}',
  'flowErrors.criterion.contributorIs': 'contributor {ref}',
  'flowErrors.criterion.requesterIsNot': 'requester not {ref}',
  'flowErrors.criterion.contributorIsNot': 'contributor not {ref}',
  'flowErrors.criterion.wearing': 'wearing "{signal}"',
  /** {tags}: `flowErrors.criterion.tagValue`, joined by `flowErrors.criterion.or`. */
  'flowErrors.criterion.tagged': 'tagged {tags}',
  'flowErrors.criterion.notTagged': 'not tagged {tags}',
  'flowErrors.criterion.tagValue': '#{tag}',
  'flowErrors.criterion.untagged': 'no tag',
  'flowErrors.criterion.hasTag': 'has a tag',
  'flowErrors.criterion.fieldEmpty': 'field "{field}" empty',
  'flowErrors.criterion.fieldIs': 'field "{field}" = {value}',
  'flowErrors.criterion.closed': 'done or cancelled',
  'flowErrors.criterion.notClosed': 'open or in review',
  'flowErrors.criterion.active': 'active',
  'flowErrors.criterion.notActive': 'not active',
  /** {level}: `flowErrors.level.*`. */
  'flowErrors.criterion.levelAtLeast': 'health at least {level}',
  'flowErrors.criterion.levelBelow': 'health below {level}',
  'flowErrors.criterion.unclaimed': 'unclaimed',
  'flowErrors.criterion.assignedTo': 'assigned to {ref}',
  'flowErrors.criterion.notAssignedTo': 'not assigned to {ref}',
  /** The `party` criterion: either seat. {ref}: the holder refs, joined by
   *  `flowErrors.criterion.or`. */
  'flowErrors.criterion.partiesInclude': 'parties include {ref}',
  'flowErrors.criterion.partiesExclude': 'parties exclude {ref}',
  'flowErrors.criterion.noParties': 'no parties',
  'flowErrors.criterion.hasParties': 'has parties',
  'flowErrors.criterion.inGroup': 'in group {group}',
  'flowErrors.criterion.notInGroup': 'not in group {group}',
  'flowErrors.criterion.noGroup': 'in no group',
  'flowErrors.criterion.inAGroup': 'in a group',
  /** A direct parent (the `parent` criterion). */
  'flowErrors.criterion.childOf': 'directly under {parent}',
  'flowErrors.criterion.notChildOf': 'not directly under {parent}',
  /** An ancestor at any depth (the `ancestor` criterion). */
  'flowErrors.criterion.under': 'under {parent}',
  'flowErrors.criterion.notUnder': 'not under {parent}',
  'flowErrors.criterion.noParent': 'no parent',
  'flowErrors.criterion.hasParent': 'has a parent',
  'flowErrors.criterion.titleContains': 'title contains "{text}"',
  'flowErrors.criterion.titleLacks': 'title does not contain "{text}"',
  'flowErrors.level.info': 'info',
  'flowErrors.level.notice': 'notice',
  'flowErrors.level.warning': 'warning',
  'flowErrors.level.critical': 'critical',
  // --- the flow surface's owner labels (src/read/flows.ts) -------------------
  /** The viewer's own context, in the owner picker and a flow's byline. */
  'flowErrors.owner.personal': 'Personal',
  /** Someone else's personal or item-anchored flow the viewer may not name. */
  'flowErrors.owner.shared': 'Shared flow',
  /** A group the read cannot name. */
  'flowErrors.owner.group': 'a group',
  // --- membership, moves and holds (src/domain/flows.ts, decisions.ts, src/server/flows.ts)
  'flowErrors.flow.notFound': 'Flow not found (or no longer active) in this org.',
  /** {stage}: the stage's stable key. */
  'flowErrors.flow.alreadyMember': 'Already a member of this flow (at "{stage}").',
  'flowErrors.flow.noStage': 'No stage "{stage}" in this flow.',
  'flowErrors.flow.noStageFieldsOnly': 'No stage "{stage}" in this fields-only flow.',
  'flowErrors.flow.notMember': 'Not a member of this flow.',
  'flowErrors.flow.notMemberOfThat': 'This item is not a member of that flow.',
  'flowErrors.flow.currentStageGone': 'Current stage "{stage}" is no longer in this flow.',
  'flowErrors.flow.alreadyAtStage': 'Already at that stage.',
  /** {error}: why the stored definition does not parse. */
  'flowErrors.flow.storedUnparsable': "This flow's stored definition no longer parses: {error}",
  /** {verb}: `flowErrors.verb.*`. */
  'flowErrors.flow.ownerOnly': 'Only an owner of the flow can {verb}.',
  'flowErrors.gate.enforcedNeedsGatekeeper':
    'The "{stage}" gate is enforced; advancing past it needs its gatekeeper.',
  'flowErrors.gate.enforcedUnmet':
    'The "{stage}" gate is enforced and its conditions are unmet; it needs the gate to pass first.',
  'flowErrors.hold.positionGone': 'Current position "{stage}" is no longer in this flow.',
  'flowErrors.hold.lastStage': 'This is the last stage; there is no crossing to hold.',
  'flowErrors.hold.needsGatekeeper': 'Holding at "{stage}" needs its gatekeeper or owner reach.',
  'flowErrors.waive.needsGatekeeper':
    'Waiving a "{stage}" gate condition needs its gatekeeper or owner reach.',
  'flowErrors.waive.notACondition': 'That ref is not a condition of this gate for this member.',
  'flowErrors.waive.already': 'Already waived.',
  'flowErrors.gateWork.needsTitle': 'Gate work needs a title.',
  'flowErrors.seeds.unknownModule': 'Work template seeds name an unknown module "{module}".',
  'flowErrors.seeds.notObject': 'Work template seeds for "{module}" must be an object.',
  'flowErrors.join.sharedNeedsOwner':
    'Joining a shared flow shares the item with its audience; that needs owner authority on the item (an owner, or its requester).',
  'flowErrors.join.viewMemberOnly': 'Only a member of this board can join its items to a flow.',

  // --- authored fields (src/domain/definitions.ts) ---------------------------
  'flowErrors.field.notObject': 'A field definition must be an object.',
  'flowErrors.field.needsKey': 'A field needs a stable key.',
  /** {field}: the field's key (needsName) or its name. */
  'flowErrors.field.needsName': 'Field "{field}" needs a name.',
  'flowErrors.field.needsDescription':
    'Field "{field}" needs a description - say what it means, not just what it stores.',
  'flowErrors.field.unknownType': 'Field "{field}": unknown type.',
  'flowErrors.field.selectionNeedsValue': 'Field "{field}": a selection needs at least one value.',
  'flowErrors.field.emptyValue': 'Field "{field}": empty value.',
  'flowErrors.field.duplicateValue': 'Field "{field}": duplicate value "{value}".',
  'flowErrors.field.valuesOnSelection': 'Field "{field}": only selections carry values.',
  'flowErrors.field.variantOnText': 'Field "{field}": only text fields carry a variant.',
  'flowErrors.field.unknownVariant': 'Field "{field}": unknown text variant.',
  'flowErrors.field.cardinalityOnSelection':
    'Field "{field}": only selections and actor fields carry a cardinality.',
  'flowErrors.field.unknownCardinality': 'Field "{field}": unknown cardinality.',
  'flowErrors.fields.notList': 'Fields must be a list.',
  'flowErrors.fields.duplicateKey': 'Duplicate field key "{field}".',
  'flowErrors.definition.payloadNotObject': 'A flow payload must be an object.',

  // --- authoring a flow (src/server/definitions.ts) ---------------------------
  /** {ref}: the anchor ref as given. */
  'flowErrors.definition.badAnchor':
    'A definition anchor must be a group, a person, or an item: {ref}',
  'flowErrors.definition.personalNeedsOrg':
    'Only a member of an org can author a personal context.',
  'flowErrors.definition.ownPersonalOnly': 'You can only {verb} for your own personal context.',
  'flowErrors.definition.anchorGroupNotFound': 'Anchor group not found.',
  'flowErrors.definition.endeavorOwnerOnly':
    'Only an owner of the endeavor can {verb} its process.',
  'flowErrors.definition.notFound': 'Definition not found.',
  'flowErrors.definition.alreadyArchived': 'Already archived.',
  'flowErrors.definition.notArchived': 'Not archived.',
  'flowErrors.definition.needsName': 'A flow needs a name.',
  'flowErrors.definition.keyTaken': 'A flow "{key}" already exists on this context.',
  'flowErrors.definition.notAFlow': 'Not a flow definition.',
  'flowErrors.definition.unchanged': 'The flow is unchanged.',
  /** What an actor tried, as a gate names it ("Only an owner of the flow can
   *  archive it."). */
  'flowErrors.verb.archive': 'archive it',
  'flowErrors.verb.unarchive': 'unarchive it',
  'flowErrors.verb.defineFlow': 'define a flow',
  'flowErrors.verb.change': 'change it',
  'flowErrors.verb.changeAccess': 'change its access',
  'flowErrors.verb.setCeremonyRole': 'set a ceremony role',
  'flowErrors.verb.closeCeremony': 'close a ceremony',
  /** {steps}: the loop's rule keys in firing order, joined with " -> ". */
  'flowErrors.cycle.definite':
    'This definition closes an automation loop that would fire regardless of state: {steps} -> (back to the start). A rule that produces its own trigger is a gate written backwards - express "must not" as an entry condition or a judgment, never as an undo.',
  'flowErrors.cycle.potential':
    'Saved with a POSSIBLE automation loop (it closes only under conditions): {steps} -> (back to the start). The runtime guards (no-undo, no-revisit, the hop budget) are the net if it ever fires.',

  // --- ceremonies and their decisions (src/domain/ceremonies.ts, src/server/ceremonies.ts)
  'flowErrors.decision.advanceNeeds': 'An advance decision needs a flow and a target stage.',
  'flowErrors.decision.holdNeeds': 'A hold decision needs a flow.',
  'flowErrors.decision.unknownOutcome': 'Unknown decision outcome "{outcome}".',
  'flowErrors.ceremony.notFound': 'Ceremony not found.',
  'flowErrors.ceremony.partyOnly': 'Only the convener or a rostered participant can {verb}.',
  'flowErrors.ceremony.needsTitle': 'A ceremony needs a title.',
  'flowErrors.ceremony.closed': 'This ceremony is closed.',
  'flowErrors.ceremony.alreadyClosed': 'This ceremony is already closed.',
  'flowErrors.ceremony.agendaItemNotFound': 'Agenda item not found.',
  'flowErrors.ceremony.alreadyApplied': 'This decision was already applied.',
  'flowErrors.ceremony.alreadyDeclined': 'This decision was already declined.',

  // --- a flow's findings (src/server/flow-findings.ts and its producers) ------
  'flowErrors.finding.flowNotFound': 'Flow definition not found.',
  'flowErrors.finding.flowsOnly': 'Findings land on flow definitions only.',
  /** {action}: the refused action, as JSON; {error}: why it was refused. */
  'flowErrors.finding.actionRefused': 'Action {action} was refused at dispatch: {error}',
  /** {policy}: `flowErrors.policy.word.*`. */
  'flowErrors.finding.actionRefusedOnQuestion':
    'Action {action} was refused at dispatch on the "{question}" question (policy: {policy}): {error}',
  /** {lane}: the lane's stable name. */
  'flowErrors.finding.cameBackOnLane':
    'Action {action} came back on the {lane} lane at dispatch, which an automation cannot answer; nothing was done.',
  'flowErrors.finding.cameBackWithQuestion':
    'Action {action} came back with a question at dispatch, which an automation cannot answer; nothing was done.',
  'flowErrors.finding.pullRefused': 'The pull to "{stage}" was refused at dispatch: {error}',
  /** {word}: the flow rule's word. */
  'flowErrors.finding.couldNotCall': 'Could not call an item "{word}": {error}',
  /** {target}: `flowErrors.target.*`. */
  'flowErrors.guard.noUndo':
    'This rule would set {target} back to the value it held just before the act that triggered the rule - it undoes its own trigger. A rule that undoes its trigger is a gate written backwards: express "must not" as an entry condition (before the act) or a dispute (after it), never as an undo.',
  'flowErrors.guard.noRevisit':
    "This cascade already set {target} to this exact value once; setting it again means the rules are circling, so the chain stops here. Check the flows acting on this item for rules that produce each other's triggers.",
  'flowErrors.guard.hopBudget':
    'This act would be machine step {hop} in a row from one originating act; the limit is {limit}. Rules this deep are almost certainly rotating through the same states - simplify the chain, or let a person act.',
  /** What a guard's planned act would set (`describeTarget`). */
  'flowErrors.target.atom': 'the "{target}" state',
  'flowErrors.target.verdict': 'the verdict',
  'flowErrors.target.tag': 'the state "{tag}"',
  'flowErrors.target.field': 'the field "{field}"',
  'flowErrors.target.position': 'the stage position',
  'flowErrors.target.other': '"{target}"',
} as const;
